import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { Location, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import {
  MatDrawer,
  MatDrawerContainer,
  MatDrawerContent,
} from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { isNil } from 'lodash-es';
import {
  catchError,
  combineLatest,
  concat,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs';
import { HasPermissionDirective, PermissionService, triggerIfSlow, WebSocketService } from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { AnswerListComponent } from '../../components/interaction-editor/components/answer-list/answer-list.component';
import { QuestionListComponent } from '../../components/interaction-editor/components/question-list/question-list.component';
import { TagPanelComponent } from '../../components/interaction-editor/components/tag-panel/tag-panel.component';
import { VariablePanelComponent } from '../../components/interaction-editor/components/variable-panel/variable-panel.component';
import { ACTION_LIST_ITEMS } from '../../components/interaction-editor/interaction-editor.data';
import { InteractionEditorStateStatus } from '../../components/interaction-editor/models/interaction-editor.model';
import { InteractionEditorStateService } from '../../components/interaction-editor/services/interaction-editor-state.service';

import { GlobalLoadersService } from '../../global-loaders.service';
import {
  ImageInteractionAnswer,
  Interaction,
  InteractionAction,
  InteractionAnswer,
  InteractionQuestion,
  InteractionStatus,
  TextInteractionAnswer,
} from '../../models/interactions.model';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { InteractionService } from '../../services/interaction.service';

import { TtsPlayerService } from '../../services/tts-player.service';
import { VarService } from '../../services/var.service';
import { ClientsService } from '../clients/services/clients.service';
import { ScmFolderWarningService } from '../client-signaling/services/scm-folder-warning.service';

export type ActionTreeNode = InteractionAction;

export type AnswerTreeNode =
  | (TextInteractionAnswer & { children: ActionTreeNode[] })
  | (ImageInteractionAnswer & { children: ActionTreeNode[] });

export interface QuestionTreeNode extends InteractionQuestion {
  children: AnswerTreeNode[];
}

const withPendingStatus =
  <T, R>(
    setStatus: (status: InteractionEditorStateStatus) => void,
    requestFn: (value: T) => Observable<R>
  ) =>
  (source: Observable<T>) =>
    source.pipe(
      tap(() => setStatus('pending')),
      switchMap((value) => requestFn(value)),
      tap(() => setStatus('idle'))
    );

@Component({
  selector: 'app-interaction-editor',
  imports: [
    CdkDrag,
    CdkDropList,
    MatDivider,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    MatTooltip,
    QuestionListComponent,
    AnswerListComponent,
    MapFromPipe,
    NgClass,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    TagPanelComponent,
    VariablePanelComponent,
    HasPermissionDirective,
  ],
  templateUrl: './interaction-editor.component.html',
  styleUrl: './interaction-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractionEditorComponent implements OnDestroy {
  private static readonly LOADER_CALLER_ID = 'interaction_editor_loader_caller';
  private readonly _dialog = inject(DialogService);
  private readonly _clientService = inject(ClientsService);
  private readonly _interactionService = inject(InteractionService);
  private readonly _interactionEditorState = inject(InteractionEditorStateService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _location = inject(Location);
  private readonly _varService = inject(VarService);
  private readonly _ttsPlayer = inject(TtsPlayerService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _scmWarningService = inject(ScmFolderWarningService);
  private readonly _wsService = inject(WebSocketService);

  private readonly _tagPanel = viewChild(TagPanelComponent);

  private readonly _fetch$ = new Subject<void>();

  private readonly _interactionId$ = this._route.paramMap.pipe(
    map((params) => params.get('interactionId')),
    filter((value) => value != null)
  );

  private readonly _selectedQuestionId =
    this._interactionEditorState.selectedQuestionId;
  private readonly _selectedAnswerId = this._interactionEditorState.selectedAnswerId;

  private readonly _interaction$ = combineLatest({
    interactionId: this._interactionId$,
    __fetch: this._fetch$.pipe(startWith(undefined)),
  }).pipe(
    takeUntilDestroyed(),

    switchMap(({ interactionId }) =>
      this._interactionService.getById(interactionId).pipe(
        triggerIfSlow(() =>
          this._globalLoaders.setActive(
            InteractionEditorComponent.LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        ),
        catchError(() => {
          this._dialog.alert({
            title: 'Foutmelding',
            message:
              'Er is iets misgegaan bij het ophalen van de interactie. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        finalize(() =>
          this._globalLoaders.setInactive(
            InteractionEditorComponent.LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        )
      )
    ),
    tap((result) => this._interactionEditorState.setState(result))
  );

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _globalLoaders = inject(GlobalLoadersService);

  protected readonly registeredAccordionsIds = computed(
    () => this.dataSource()?.answers.map((answer) => answer._id) ?? []
  );

  protected readonly actionListItems = computed(() =>
    ACTION_LIST_ITEMS.filter(
      (item) =>
        !item.requiredPermission ||
        this._permissionService.hasPermission(item.requiredPermission)
    )
  );

  protected readonly dataSource = this._interactionEditorState.interaction;

  private readonly _client$ = toObservable(
    this._interactionEditorState.interaction
  ).pipe(
    filter(
      (result): result is Interaction => !isNil(result) && result.clientId != null
    ),
    map((result) => result.clientId!),
    switchMap((clientId) => this._clientService.getCachedClient(clientId)),
    tap((client) => this._varService.setBuiltinVar('roepnaam', client.nickname)),
    catchError(() => EMPTY)
  );

  protected readonly client = toSignal(this._client$);

  protected readonly isLocked = computed(
    () =>
      this.dataSource()?.status === 'published' ||
      this.dataSource()?.isShared === true ||
      !this._permissionService.hasPermission('interaction:update')
  );

  protected readonly dataSourceTree = computed(() => {
    const questions = this.dataSource()?.questions ?? [];
    const answers = this.dataSource()?.answers ?? [];
    const actions = this.dataSource()?.actions ?? [];

    return this._buildInteractionTree(questions, answers, actions);
  });

  protected readonly selectedQuestionAnswers = computed(() => {
    const target = this.dataSourceTree().find(
      (question) => question._id == this._selectedQuestionId()
    );

    if (!target) {
      return [];
    }

    return target.children;
  });

  protected readonly statusMapping: Record<InteractionStatus, string> = {
    published: 'Gepubliceerd',
    draft: 'Concept',
  };

  protected readonly registeredAccordionIds = computed(
    () => this.dataSource()?.answers.map((answer) => answer._id) ?? []
  );

  protected readonly questionEvents$ = this._interactionEditorState.events$.pipe(
    filter((event) => event.entity === 'questions')
  );

  protected readonly questionCreateEvent$ = this.questionEvents$.pipe(
    filter((event) => event.action === 'create')
  );

  protected readonly questionUpdateEvent$ = this.questionEvents$.pipe(
    filter((event) => event.action === 'update')
  );

  protected readonly questionDeleteEvent$ = this.questionEvents$.pipe(
    filter((event) => event.action === 'delete')
  );

  protected readonly questionSortEvent$ = this.questionEvents$.pipe(
    filter((event) => event.action === 'sort')
  );

  protected readonly answerEvents$ = this._interactionEditorState.events$.pipe(
    filter((event) => event.entity === 'answers')
  );

  protected readonly answerCreateEvents$ = this.answerEvents$.pipe(
    filter((event) => event.action === 'create')
  );

  protected readonly answerUpdateEvents$ = this.answerEvents$.pipe(
    filter((event) => event.action === 'update')
  );

  protected readonly answerDeleteEvents$ = this.answerEvents$.pipe(
    filter((event) => event.action === 'delete')
  );

  protected readonly answerSortEvent$ = this.answerEvents$.pipe(
    filter((event) => event.action === 'sort')
  );

  protected readonly actionEvents$ = this._interactionEditorState.events$.pipe(
    filter((event) => event.entity === 'actions')
  );

  protected readonly actionCreateEvents$ = this.actionEvents$.pipe(
    filter((event) => event.action === 'create')
  );

  protected readonly actionUpdateEvents$ = this.actionEvents$.pipe(
    filter((event) => event.action === 'update')
  );

  protected readonly actionSortEvent$ = this.actionEvents$.pipe(
    filter((event) => event.action === 'sort')
  );

  protected readonly actionDeleteEvents$ = this.actionEvents$.pipe(
    filter((event) => event.action === 'delete')
  );

  protected readonly currentRoutePath = toSignal(
    this._route.url.pipe(
      map((urlSegments) => urlSegments.at(0)),
      filter(Boolean),
      map((segment) => segment.path as 'interactions' | 'templates')
    )
  );

  constructor() {
    this._interaction$.subscribe();

    const createQuestion$ = this.questionCreateEvent$.pipe(
      map((event) => event.value),
      filter((value) => value != null),
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([value, interactionId]) =>
          this._interactionService
            .createQuestion(interactionId, {
              value,
            })
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het aanmaken van het spraakbericht. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
      )
    );

    const updateQuestion$ = this.questionUpdateEvent$.pipe(
      filter(() => this._selectedQuestionId() != null),
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService
            .updateQuestion(interactionId, data.id, {
              value: data.value,
            })
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het bewerken van het spraakbericht. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
      )
    );

    const deleteQuestion$ = this.questionDeleteEvent$.pipe(
      filter(() => this._selectedQuestionId() != null),
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) => {
          const orphanedActions = (
            this._interactionEditorState.interaction()?.actions ?? []
          ).filter(
            (action) => action.type === 'goto-question' && action.value === data.id
          );

          return this._interactionService.deleteQuestion(interactionId, data.id).pipe(
            switchMap(() => {
              if (orphanedActions.length === 0) {
                return of(null);
              }

              return concat(
                ...orphanedActions.map((action) =>
                  this._interactionService.deleteAction(interactionId, action._id).pipe(
                    map(() => true),
                    catchError(() => of(false))
                  )
                )
              ).pipe(
                toArray(),
                tap((results) => {
                  const failedCount = results.filter((success) => !success).length;

                  if (failedCount > 0) {
                    this._dialog.alert({
                      title: 'Foutmelding',
                      message: `${failedCount} 'Ga naar vraag'-${failedCount === 1 ? 'actie kon' : 'acties konden'} niet worden verwijderd. Verwijder ${failedCount === 1 ? 'deze actie' : 'deze acties'} handmatig.`,
                      confirmText: 'OK',
                    });
                  }
                })
              );
            }),
            catchError(() => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het verwijderen van het spraakbericht. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return of(null);
            }),
            finalize(() => {
              this._selectedQuestionId.set(null);
              this._selectedAnswerId.set(null);
            })
          );
        }
      )
    );

    const sortQuestion$ = this.questionSortEvent$.pipe(
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService
            .sortQuestion(interactionId, data.id, { index: data.index })
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Sorteren mislukt',
                  message:
                    'Er is iets misgegaan bij het sorteren van spraakberichten. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
      )
    );

    const createAnswer$ = this.answerCreateEvents$.pipe(
      filter((event) => event.value != null && this._selectedQuestionId() != null),
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([event, interactionId]) => {
          const payload:
            | { type: 'image'; value: string; questionId: string; fullScreen?: boolean }
            | { type: 'text'; value: string; questionId: string } =
            event.type === 'image' && typeof event.value === 'object'
              ? {
                  type: 'image',
                  value: event.value.mediaId,
                  questionId: this._selectedQuestionId() as string,
                  fullScreen: event.fullScreen,
                }
              : {
                  type: 'text',
                  value: event.value as string,
                  questionId: this._selectedQuestionId() as string,
                };

          return this._interactionService.createAnswer(interactionId, payload).pipe(
            catchError(() => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het aanmaken van het antwoord. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return of(null);
            })
          );
        }
      )
    );

    const updateAnswer$ = this.answerUpdateEvents$.pipe(
      filter(
        () => this._selectedQuestionId() != null && this._selectedAnswerId() != null
      ),
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) => {
          const payload:
            | { type: 'image'; value: string; questionId: string; fullScreen?: boolean }
            | { type: 'text'; value: string; questionId: string } =
            data.type === 'image' && typeof data.value === 'object'
              ? {
                  type: 'image',
                  value: data.value.mediaId,
                  questionId: this._selectedQuestionId() as string,
                  fullScreen: data.fullScreen,
                }
              : {
                  type: 'text',
                  value: data.value as string,
                  questionId: this._selectedQuestionId() as string,
                };

          return this._interactionService
            .updateAnswer(interactionId, data.id, payload)
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het bewerken van het antwoord. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            );
        }
      )
    );

    const deleteAnswer$ = this.answerDeleteEvents$.pipe(
      filter(
        () => this._selectedQuestionId() != null && this._selectedAnswerId() != null
      ),
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService.deleteAnswer(interactionId, data.id).pipe(
            catchError(() => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het verwijderen van het antwoord. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return of(null);
            }),
            finalize(() => this._selectedAnswerId.set(null))
          )
      )
    );

    const sortAnswer$ = this.answerSortEvent$.pipe(
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService
            .sortAnswer(interactionId, data.id, {
              index: data.index,
            })
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Sorteren mislukt',
                  message:
                    'Er is iets misgegaan bij het sorteren van antwoorden. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
      )
    );

    const createAction$ = this.actionCreateEvents$.pipe(
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService
            .createAction(interactionId, {
              ...data.value,
              index: data.index,
              questionId: this._selectedQuestionId(),
              answerId: this._selectedAnswerId(),
            })
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het aanmaken van een actie. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
      )
    );

    const updateAction$ = this.actionUpdateEvents$.pipe(
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService
            .updateAction(interactionId, data.id, {
              ...data.value,
              questionId: this._selectedQuestionId(),
              answerId: this._selectedAnswerId(),
            })
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het bewerken van de actie. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
      )
    );

    const deleteAction$ = this.actionDeleteEvents$.pipe(
      filter(
        () => this._selectedQuestionId() != null && this._selectedAnswerId() != null
      ),
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService.deleteAction(interactionId, data.id).pipe(
            catchError(() => {
              this._dialog.alert({
                title: 'Sorteren mislukt',
                message:
                  'Er is iets misgegaan bij het verwijderen van de actie. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return of(null);
            })
          )
      )
    );

    const sortAction$ = this.actionSortEvent$.pipe(
      withLatestFrom(this._interactionId$),
      withPendingStatus(
        (status) => this._interactionEditorState.setStatus(status),
        ([data, interactionId]) =>
          this._interactionService
            .sortAction(interactionId, data.id, { index: data.index })
            .pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Sorteren mislukt',
                  message:
                    'Er is iets misgegaan bij het sorteren van acties. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
      )
    );

    merge(
      createQuestion$,
      updateQuestion$,
      deleteQuestion$,
      sortQuestion$,
      createAnswer$,
      updateAnswer$,
      deleteAnswer$,
      sortAnswer$,
      createAction$,
      updateAction$,
      deleteAction$,
      sortAction$
    )
      .pipe(
        filter(() => !this.isLocked()),
        tap(() => this._fetch$.next()),
        takeUntilDestroyed()
      )
      .subscribe();

    // Kick user out of the editor when a move is initiated on this template
    this._wsService
      .connect<{ correlationIds?: string[] }>('/event-stream', {
        requireAuth: true,
        topics: ['unit/+/interaction/changed'],
      })
      .pipe(
        switchMap((connection) => connection.events$),
        filter(
          (event) =>
            event.eventType === 'interaction.move.started' &&
            Array.isArray(event.payload?.correlationIds)
        ),
        filter((event) => {
          const currentCorrelationId =
            this.dataSource()?.correlationId;
          if (!currentCorrelationId) return false;
          return event.payload.correlationIds!.includes(
            currentCorrelationId
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this._dialog.alert({
          title: 'Template wordt verplaatst',
          message:
            'Een andere gebruiker is begonnen met het verplaatsen van deze template. ' +
            'Je wordt teruggestuurd naar het overzicht.',
          confirmText: 'OK',
        });
        this.goBack();
      });
  }

  protected goBack(): void {
    if (window.history.length <= 1) {
      const path = this.currentRoutePath();
      if (path === 'templates') {
        this._router.navigate(['templates']);
      } else if (path === 'interactions') {
        const clientId = this.dataSource()?.clientId;
        if (clientId) {
          this._router.navigate(['clients', clientId, 'interactions']);
        }
      }
      return;
    }
    this._location.back();
  }

  ngOnDestroy() {
    this._interactionEditorState.clearState();
    this._varService.clearClientVars();
    this._varService.clearBuiltinVars();
    this._ttsPlayer.clearCache();
  }

  handleInteractionPublish() {
    if (this.isLocked()) {
      console.warn('Unable to publish interaction. Editor is locked');
      return;
    }

    const path = this.currentRoutePath();

    if (!path) {
      console.log('Unable to publish interaction. Path is not set');
      return;
    }

    const publishConfig = {
      interactions: {
        dialog: {
          title: 'Interactie publiceren?',
          message:
            'Na publicatie kan de interactie gebruikt worden binnen de kalender en signaleringsmodule. ' +
            'Alle gekoppelde interacties worden automatisch bijgewerkt. ' +
            'Deze actie kan niet ongedaan worden gemaakt',
        },
        errorMessage:
          'Er is iets misgegaan bij het publiceren van de interactie. Probeer het later nog eens',
      },
      templates: {
        dialog: {
          title: 'Template publiceren?',
          message:
            'Na publicatie kan de template gekozen worden bij cliënt interacties. ' +
            'Wijzigingen worden niet automatisch doorgevoerd in interacties die gekozen zijn vanuit templates. ' +
            'Deze actie kan niet ongedaan worden gemaakt',
        },
        errorMessage:
          'Er is iets misgegaan bij het publiceren van de template. Probeer het later nog eens',
      },
    };

    const currentConfig = publishConfig[path];

    const publish$ = this._interactionId$.pipe(
      takeUntilDestroyed(this._destroyRef),
      switchMap((interactionId) =>
        this._interactionService.publish(interactionId).pipe(
          map(() => true as const),
          catchError(() => {
            this._dialog.alert({
              title: 'Foutmelding',
              message: currentConfig.errorMessage,
              confirmText: 'OK',
            });

            return of(false as const);
          })
        )
      ),
      tap((success) => {
        if (!success) return;

        const interaction = this.dataSource();
        const tagPanel = this._tagPanel();

        // Only warn if tags were removed between publications, since only
        // tag removals can cause an SCM block to drop to 0 matches.
        if (path !== 'templates' && interaction?.clientId && tagPanel) {
          const initialIds = new Set(tagPanel.initialTagIds());
          const currentIds = new Set(tagPanel.assignedTagIds());
          const removedTagIds = [...initialIds].filter((id) => !currentIds.has(id));

          if (removedTagIds.length > 0) {
            this._scmWarningService
              .checkAndWarn(interaction.clientId, removedTagIds)
              .subscribe();
          }
        }

        this._location.back();
      })
    );

    this._dialog.confirm({
      title: currentConfig.dialog.title,
      message: currentConfig.dialog.message,
      confirmText: 'Publiceren',
      cancelText: 'Annuleren',
      onConfirm: () => publish$,
    });
  }

  handleInteractionReject() {
    if (this.isLocked()) {
      console.warn('Unable to reject interaction. Editor is locked');
      return;
    }

    this._interactionId$
      .pipe(
        switchMap((interactionId) =>
          this._interactionService.reject(interactionId).pipe(
            catchError(() => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het afkeuren van de interactie. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return of(null);
            })
          )
        ),
        tap(() => this._fetch$.next())
      )
      .subscribe();
  }

  handleNameEdit(): void {
    const interaction = this.dataSource();
    if (!interaction || this.isLocked()) return;

    const path = this.currentRoutePath();
    const singular = path === 'templates' ? 'template' : 'interactie';

    this._dialog.prompt(
      {
        title: 'Naam wijzigen',
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        onConfirm: (result) =>
          of(result).pipe(
            filter((value) => value != null),
            switchMap((label) =>
              this._interactionService.update(interaction._id, { label }).pipe(
                tap(() => this._fetch$.next()),
                catchError((error) => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message: `Er is iets misgegaan bij het bewerken van de naam. Probeer het later nog eens`,
                    confirmText: 'OK',
                  });
                  return of(error);
                })
              )
            )
          ),
        input: {
          mode: 'input',
          placeholder: `Naam ${singular}`,
          defaultValue: interaction.label,
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een naam in',
            },
            pattern: {
              validator: Validators.pattern(/^[^\\/:*?"<>|]*$/),
              errorMessage:
                'De volgende tekens zijn niet toegestaan: \\ / : * ? " < > |',
            },
            maxlength: {
              validator: Validators.maxLength(100),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;
                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  protected goToFlowView(): void {
    const id = this.dataSource()?._id;
    const path = this.currentRoutePath();
    if (!id || !path) return;
    window.open(this._location.prepareExternalUrl(`${path}/${id}/flow`), '_blank');
  }

  private _buildInteractionTree(
    questions: InteractionQuestion[],
    answers: InteractionAnswer[],
    actions: InteractionAction[]
  ): QuestionTreeNode[] {
    return questions
      .sort((a, b) => a.index - b.index)
      .map((question) => ({
        ...question,
        children: answers
          .filter((answer) => answer.questionId === question._id)
          .sort((a, b) => a.index - b.index)
          .map((answer) => ({
            ...answer,
            children: actions
              .filter((action) => action.answerId === answer._id)
              .sort((a, b) => a.index - b.index),
          })),
      }));
  }
}
